import type { Leaderboard, SlidePosition, Tally } from "@hali-shares/deck-schema";
import { createContext, useContext } from "react";
import type { Theme } from "../live/theme";
import type { FlowLayout } from "./flow/layout";
import type { DeckModel } from "./model";

export type PollUiState = { locked: boolean; revealed: boolean };

export type PresenterCtx = {
  model: DeckModel;
  layouts: Map<string, FlowLayout>;
  /** `${PUBLIC_URL}/j`, shown as text and encoded in the QR. */
  joinUrl: string;
  /** Live session code; null until sessions exist (Phase 2). */
  code: string | null;
  position: SlidePosition;
  goto: (pos: SlidePosition) => void;
  gotoReferences: () => void;
  poll: (blockId: string) => PollUiState;
  togglePoll: (blockId: string, key: keyof PollUiState) => void;
  /** Live counts for a poll (presenter only); undefined in rehearsal or before the first vote arrives. */
  tally: (blockId: string) => Tally | undefined;
  /** null in rehearsal (no live session). */
  live: { connected: boolean; ended: boolean; end: () => void; restart: () => void; csvUrl: string } | null;
  /** Quiz leaderboard (live sessions); null in rehearsal. */
  leaderboard: Leaderboard | null;
  /** Rehearsal clock, per section. */
  clock: { started: boolean; running: boolean; times: Record<string, number>; toggle: () => void; reset: () => void };
  theme: Theme;
  setTheme: (t: Theme) => void;
  /** D: the next theme in the list. */
  cycleTheme: () => void;
  openNotes: () => void;
  /** Rendering a studio preview: no timer, no session. */
  preview?: boolean;
};

export const PresenterContext = createContext<PresenterCtx | null>(null);

export function usePresenter(): PresenterCtx {
  const ctx = useContext(PresenterContext);
  if (!ctx) throw new Error("usePresenter outside PresenterContext");
  return ctx;
}

export function layoutKey(blockId: string, nodeId?: string): string {
  return nodeId ? `${blockId}/${nodeId}` : blockId;
}
